import { useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";

import { Modal } from "../../components/Modal/Modal";
import { Input } from "../../components/Input/Input";
import { Button } from "../../components/Button/Button";
import { Alert } from "../../components/Alert/Alert";
import { useToast } from "../../components/Toast/toastContext";
import { ApiError, describeError } from "../../lib/errors";
import { deleteProject } from "./api";
import styles from "./DeleteProjectModal.module.css";

interface Props {
  open: boolean;
  onClose: () => void;
  /** The project to delete — id + name + etag (etag guards the DELETE). */
  project: { id: string; name: string; etag: string };
  /** Called after a successful delete — caller navigates away + toasts. */
  onDeleted: () => void;
}

/**
 * Type-to-confirm modal for permanently deleting a project. Mirrors
 * RotateConfirmModal — a danger-coloured warn icon in the header, an
 * irreversible danger alert, and a project-name confirm input that enables
 * the delete button only on an exact match.
 */
export function DeleteProjectModal({
  open,
  onClose,
  project,
  onDeleted,
}: Props) {
  const { push } = useToast();
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const projectName = project.name;
  const matches = typed.trim() === projectName && projectName.length > 0;

  function reset() {
    setTyped("");
  }

  function handleClose() {
    if (deleting) return;
    reset();
    onClose();
  }

  async function confirm() {
    if (!matches || deleting) return;
    setDeleting(true);
    try {
      // Strip weak-validator + surrounding quotes before sending the raw token.
      const raw = (project.etag ?? "")
        .replace(/^W\//, "")
        .replace(/^"|"$/g, "");
      await deleteProject(project.id, raw);
      reset();
      onDeleted();
    } catch (err) {
      if (err instanceof ApiError && err.code === "etag_mismatch") {
        push({
          variant: "warn",
          message:
            "Project was updated by someone else — reload before deleting.",
        });
        onClose();
        return;
      }
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    } finally {
      setDeleting(false);
    }
  }

  const title = projectName ? `Delete ${projectName}?` : "Delete project?";

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => void confirm()}
            disabled={!matches || deleting}
            loading={deleting}
            icon={<Trash2 size={14} aria-hidden="true" />}
          >
            Delete project
          </Button>
        </>
      }
    >
      <div className={styles.warnIcon} aria-hidden="true">
        <AlertTriangle size={18} />
      </div>

      <div className={styles.alertWrap}>
        <Alert variant="danger" title="This cannot be undone.">
          This permanently deletes the project, its API key, all team
          memberships, pending invites, and every player, guild, chat message
          and help request.
        </Alert>
      </div>

      <div className={styles.confirmLabelRow}>
        <span className={styles.confirmLabelText}>
          Type{" "}
          <span className={styles.confirmQuote}>{projectName}</span> to confirm
        </span>
      </div>

      <Input
        aria-label={`Type ${projectName} to confirm`}
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoFocus
        className={`${styles.confirmInput} ${matches ? styles.confirmInputOk : ""}`}
        hint={matches ? "✓ Project name matches" : undefined}
        helperVariant={matches ? "ok" : "default"}
      />
    </Modal>
  );
}
